/**
 * D-020 V2B — the explicit hotkey-focus model (LT2-013).
 *
 * Exactly ONE derivative card can be hotkey-focused at a time. A card
 * registers a RESOLVER, not a snapshot: the resolver ref is updated every
 * render with exactly the values the card just painted, which is the
 * mechanism that makes "A/D submit the same displayed top-of-book action a
 * mouse click would" true by construction — the listener can never read a
 * price the trader isn't looking at.
 *
 * Card ids are market-identity-stable (`st:<eventKey>:spread`,
 * `gl:<gameKey>:<period>:total`), never positional — a re-sorted board
 * cannot silently retarget the focus (LT2-006). Unregistering (unmount,
 * layout flip, tab/period change re-keys the id) clears the focus if it was
 * held, so a hotkey can never fire at a card that is no longer on screen.
 *
 * ── Pre-send facts (speed-demon Deploy C) ────────────────────────────────
 * The keydown path used to learn three page-level facts by walking the whole
 * document on every press: "is a modal open?", "does exactly one card paint
 * the focus mark?", and "is the focused row on screen?" — the last one via a
 * forced synchronous layout. Those facts are now PUBLISHED here at the moment
 * they change (modal mount, card commit, intersection change), so the press
 * reads flags instead of measuring. Every flag is fail-closed: an absent or
 * stale flag sends the listener down the exact document/rect path it took
 * before, never to "assume visible".
 *
 *   modalOpenCount        — atoms/Modal mounts/unmounts (`noteModalOpened`).
 *   focusPaintAmbiguous   — some card OTHER than the focused one rendered
 *                           itself focused (`notePainted`, from useLt2Card's
 *                           post-commit effect with the boolean it rendered).
 *   card root             — the lowest common ancestor of the rows a card
 *                           paints, so the price-row lookup can scope its
 *                           query (`setCardRoot` / `focusedCardRoot`).
 * ── The level cursor (Sniper v2 Phase 2, docs/260909SniperV2Design.md §4.1) ─
 * The focused card is WHICH card; the cursor is WHERE on it. `cursor.level`
 * (0 = top of book) is the rung every LT2 order key acts at, `cursor.slot` is
 * the book the highlight is drawn on. It is navigation state only: it can never
 * widen what a key may do, because the firing path still proves the rung twice
 * — the resolver must publish a displayed level there (`Lt2DisplayedLevel`) and
 * the DOM row must agree (`data-lt2-level` / `data-lt2-level-price`). A focus
 * change resets it, so a cursor cannot outlive the card it was aimed at.
 *
 *   row visibility        — per ROW element (the fence proves the PRICE ROW,
 *                           not the card: an expanded ladder row can sit
 *                           under the fold while the card's top is on
 *                           screen), stamped by an IntersectionObserver and
 *                           invalidated by any scroll/resize since.
 */
import { create } from 'zustand';
import type { GameRow, VenueBook } from '@/views/ValueDashboard/types';
import type { LineAvailability } from '@/lib/sport/lineAvailability';
import type { Lt2Period, Lt2Surface } from '@/lib/trading/liveIntent';
import type { Lt2AttackTarget } from '@/lib/trading/attackTarget';
import { queueDepth, rankRestingOrders } from '@/views/ValueDashboard/ladder/helpers';

/**
 * Speed-demon D1 — the ONE resting order a jump key (Q/R) would re-price on
 * this slot. Resolved by the card from the same `userOrdersFor(ticker)` prop
 * its chips render from, so the order the jump moves is the order the trader
 * can see resting on the ladder.
 */
export interface Lt2RestingOrderRef {
  orderId: string;
  /** Side-native cents the order rests at now (the "from" half of the ack). */
  priceCents: number;
  remainingCount: number;
}

/** The minimum an own-order row must carry for `resolveRestingTarget`. A
 *  structural subset of `UserOrderSlice` (hooks/useUserKalshiPOS) so the
 *  store does not import the hook module (which imports accountStore). */
export interface Lt2RestingOrderRow {
  orderId: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  priceCents: number;
  remainingCount: number;
  queuePosition: number | null;
  queueRetained?: boolean;
}

/**
 * Which of the trader's resting orders on (ticker, side) a jump may move.
 *
 * Rules, fail-closed:
 *   • candidates = the trader's resting BUY orders on that side with
 *     remaining > 0 (a jump re-prices a passive post; sells and drained rows
 *     are never candidates);
 *   • 0 candidates → `resting: null` (the listener acks "No resting order");
 *   • exactly 1 → that order;
 *   • more than 1 → `resting: null` + `restingAmbiguous: true` — the
 *     listener acks "pull first". Guessing which of several to move would
 *     silently re-queue an order the trader did not mean, so the release
 *     rule is one-or-nothing. The candidates are still ordered by
 *     `rankRestingOrders` (nearest the front of the queue first) so a later
 *     "jump the front one" relaxation is a one-line change here, not a new
 *     ordering.
 */
export function resolveRestingTarget(
  orders: readonly Lt2RestingOrderRow[] | null | undefined,
  side: 'yes' | 'no',
  book: VenueBook | null,
): Pick<Lt2HotkeyTarget, 'resting' | 'restingAmbiguous'> {
  const candidates = (orders ?? []).filter(
    (o) =>
      o.side === side &&
      o.action === 'buy' &&
      Number.isFinite(o.remainingCount) &&
      o.remainingCount > 0 &&
      Number.isFinite(o.priceCents),
  );
  if (candidates.length === 0) return { resting: null, restingAmbiguous: false };
  const ranked = rankRestingOrders(
    candidates.map((o) => ({
      orderId: o.orderId,
      remainingCount: o.remainingCount,
      queueOrBetter: queueDepth(o, book).atLevelOrBetter,
      tooltip: '',
      queueRetained: o.queueRetained,
    })),
  );
  if (ranked.length > 1) return { resting: null, restingAmbiguous: true };
  const front = candidates.find((o) => o.orderId === ranked[0].orderId) ?? candidates[0];
  return {
    resting: {
      orderId: front.orderId,
      priceCents: front.priceCents,
      remainingCount: front.remainingCount,
    },
    restingAmbiguous: false,
  };
}

/**
 * Sniper v2 Phase 2 — ONE displayed rung of a slot's addressable ladder.
 *
 * Published by the card's resolver from exactly the rows it painted, index 0
 * first (level 0 === top of book === `Lt2HotkeyTarget.priceCents`). The listener
 * never derives a level price itself: a price the trader is not looking at must
 * never reach an order, and a level is only a price the trader is looking at if
 * the card says it painted one there AND the DOM row agrees (`data-lt2-level`).
 *
 * `attack` is the bounded aggressive target FOR THIS RUNG, same shape and same
 * rule as `Lt2HotkeyTarget.attack`: resolver-computed, never recomputed here.
 * Absent/null at a level > 0 means W/E refuse truthfully at that level.
 */
export interface Lt2DisplayedLevel {
  priceCents: number;
  /** Displayed resting size at the rung; null when the surface hides it. */
  size: number | null;
  attack?: Lt2AttackTarget | null;
}

/** Which of the focused card's two books/sides the level cursor addresses —
 *  the same 'a' (top/left) and 'd' (bottom/right) slots the fence proves. */
export type Lt2CursorSlot = 'a' | 'd';

/**
 * The level cursor: WHERE on the focused card the LT2 keys act.
 *
 * `level` is card-wide (0 = top of book) and `slot` is the book the highlight
 * is drawn on. A/D/W/E/Q/R act on THEIR OWN slot at `level`, so the two keys
 * stay two different sides of the same rung — H/L only move which side carries
 * the visible cursor, never which side a key posts on.
 */
export interface Lt2Cursor {
  level: number;
  slot: Lt2CursorSlot;
}

export const LT2_CURSOR_DEFAULT: Lt2Cursor = { level: 0, slot: 'a' };

/**
 * Ceiling used when the focused card publishes no `levels` for the cursor's
 * slot: the ultrawide execution band's 12 rungs per side (design §4.1). It only
 * bounds the CURSOR; firing still needs a published level and a painted row, so
 * a cursor parked past the end of a shorter ladder refuses truthfully rather
 * than firing at a rung nobody can see.
 */
export const LT2_CURSOR_MAX_LEVEL = 11;

/**
 * One leg an ARMED sequence would send, painted on the ladder before it fires
 * (design §4.1 "ghost legs"): what you see is exactly what `order-sequence`
 * will send. Set by the composer (a sibling lane owns the op builder); this
 * store owns the shape, the setter and the clear.
 *
 * `book` is how the EXECUTION BAND names the book the leg lands on — the market
 * ticker by default, or whatever stable id the surface tells its ladder to
 * answer to (`Lt2LadderAddressing.book`). `level` is the rung index on that
 * book's addressable ladder, or null for a leg the composer addressed by PRICE
 * alone (a resting blocker whose rung is off the displayed ladder).
 *
 * A row paints the ghost only when the book AND the displayed cents match, plus
 * the level when the leg names one — so a mirrored YES/NO rung can never wear a
 * tint meant for the other side, and a price-addressed leg still lands on the
 * rung that is actually showing that price.
 *
 * Structurally a superset of the composer's own `GhostLeg`
 * (`stores/sequenceComposerStore.ts`), so its plan can be handed to
 * `setGhostLegs` unchanged — one vocabulary for what a plan looks like, rather
 * than a translation layer that can disagree with the request.
 */
export interface Lt2GhostLeg {
  book: string;
  level: number | null;
  priceCents: number;
  role: 'take' | 'blocker' | 'kill';
  /** Contracts the leg will carry, when the composer knows it; display only. */
  count?: number | null;
}

/** Which lines board the execution band is showing (design §4.1 tab strip).
 *  Store-owned so the `Z` key has something real to cycle before the cockpit
 *  grids land; the cockpit renders from it. */
export type Lt2MarketTab = 'ml' | 'spr' | 'tot';
const LT2_MARKET_TAB_ORDER: readonly Lt2MarketTab[] = ['ml', 'spr', 'tot'];

/** The standard-viewport dock page (design §3): context ⇄ control on `` ` ``. */
export type Lt2DockPage = 'context' | 'control';

export interface Lt2HotkeyTarget {
  book: VenueBook | null;
  side: 'yes' | 'no';
  /** EXACTLY the cents the card is displaying for this action; null = no
   *  displayed price → the hotkey refuses truthfully. */
  priceCents: number | null;
  /** The displayed action's maker semantics (ladder bid = join = true;
   *  pill = the section's price mode). */
  postOnly: boolean;
  /** #724 — the displayed price IS this side's own best bid (a join), so a
   *  post-only order at it rests rather than crosses. Two-sided S posts only
   *  joins. Deliberately separate from `postOnly`: the moneyline pills submit
   *  postOnly:false in every price mode (A/D keep mouse parity), so
   *  `postOnly` cannot say whether a moneyline price is a bid. */
  joinsBid: boolean;
  label: string;
  availability: LineAvailability;
  /** Live Trading V3 — the bounded aggressive action for this same side:
   *  reference (best same-side displayed bid) and target (reference + the
   *  accepted depth). Resolved from exactly the book the card is painting,
   *  like every other field here, so W/E can never act on a price the trader
   *  isn't looking at. Null = no attackable reference right now (no
   *  displayed bid, price hidden, or target out of range) → W/E refuse
   *  truthfully. */
  attack: Lt2AttackTarget | null;
  /** Speed-demon D1 — the ONE resting order the jump key on this slot would
   *  re-price to `priceCents`. Three states, each read by the listener:
   *    absent   — this surface does not resolve resting orders (the
   *               dashboard/GameDetail cards today) → Q/R refuse as
   *               unsupported here, never as "no order";
   *    null     — resolved, and there is no single candidate (none, or
   *               several when `restingAmbiguous` is set);
   *    a ref    — exactly one candidate, from `resolveRestingTarget`. */
  resting?: Lt2RestingOrderRef | null;
  /** True when more than one resting BUY order sits on this slot, so the
   *  jump refuses with "pull first" instead of picking one. */
  restingAmbiguous?: boolean;
  /**
   * Phase 2 — the rungs of this slot the LEVEL CURSOR may address, top of book
   * first. `levels[0].priceCents` must equal `priceCents`; the listener refuses
   * on disagreement rather than picking one.
   *
   * ABSENT is the default and means "this surface does not address levels": the
   * cursor may only fire at level 0 there and every pre-Phase-2 path behaves
   * byte-identically. A surface that publishes levels must also paint the
   * `data-lt2-level` marks (see `Lt2HotkeyListener.resolveCursorLevel`) — the
   * resolver and the DOM are two independent witnesses and both must agree.
   */
  levels?: readonly Lt2DisplayedLevel[];
}

export interface Lt2CardTargets {
  /** A = top (vertical pill pair) / LEFT (side-by-side ladder columns). */
  a: Lt2HotkeyTarget | null;
  /** D = bottom / RIGHT. */
  d: Lt2HotkeyTarget | null;
  surface: Lt2Surface;
  marketKind: 'moneyline' | 'spread' | 'total';
  period: Lt2Period;
  line: number | null;
  /** Compact human name for the header target readout ("NYJ ML",
   *  "DAL −3.5", "O/U 47.5"). Presentation metadata only — identity stays
   *  the card id, prices stay the a/d targets. */
  cardLabel: string;
  /** V3 Pull All scope: the focused card's own GAME, as the authoritative
   *  input to the exact-game cancel selection (canonical-key resolution over
   *  live order state — never DOM). Resolver-provided so it is always the
   *  game the trader is looking at. Null = this card cannot anchor a
   *  game-scoped pull, and Spacebar refuses truthfully. */
  pullScope: { game: GameRow } | null;
  /** Presentation-only pause: 'expanded' = the #647 moneyline suppression
   *  (the card keeps its focus, A/D refuse until collapse). Never consulted
   *  by the firing path — the DOM price marks remain the fence's authority. */
  pausedReason: 'expanded' | null;
  /** #724 — the card's game has a third outcome (a soccer Draw). A and D
   *  still target the two teams and S posts both, but NO on both teams of a 1X2
   *  pays on a draw either way, so the pair is a draw position rather than a
   *  two-sided market. Absent = two outcomes. */
  threeWay?: boolean;
}

/**
 * One row's last browser-measured visibility, by the SAME definition the
 * rect path uses (CSS-visible AND intersecting the viewport), stamped in
 * epoch ms at the IntersectionObserver notification that measured it.
 */
export interface Lt2RowVisibility {
  visible: boolean;
  atMs: number;
}

type Resolver = () => Lt2CardTargets | null;

interface Lt2FocusStore {
  focusedCardId: string | null;
  /** Monotonic presentation-invalidation signal. Bumped by the FOCUSED
   *  card's own re-render (useLt2Card), so the header target readout can
   *  re-derive label/paused state from `resolveFocused()` without polling
   *  and without a second target source. Never read by the firing path. */
  presentationSeq: number;
  notePresentation: () => void;
  setFocus: (id: string) => void;
  /** #648 — the mouse contract. The card's own focus region is the ONE
   *  gesture that changes focus: same card releases, another card takes it.
   *  One store update, so a consumer can never compose a stale
   *  get/clear/set and land two cards claiming focus (which the hotkey
   *  fence treats as ambiguous and refuses). */
  toggleFocus: (id: string) => void;
  /** #648 — a line chip picks a market INSIDE its own card, so it may take a
   *  FREE focus, but it is not the card's focus region: it must never steal
   *  the focus from another card and must never clear. Without this a nested
   *  control could still retarget the hotkeys incidentally, which is the
   *  capture-phase bug #648 exists to end. */
  focusIfFree: (id: string) => void;
  clearFocus: () => void;
  /** #608 — drop the focus only if THIS card holds it. For the case the
   *  unregister teardown cannot cover: the card stays mounted under the same
   *  id while the market it was displaying disappears, so nothing
   *  unregisters, yet the held focus no longer names a market the trader
   *  chose. Equality-guarded so one card can never clear another's focus.
   *  D19 — callers clearing because the market disappeared pass
   *  `{ marketGone: true }`; the aim chip's own ✕ does not. */
  clearFocusIf: (id: string, opts?: { marketGone?: boolean; lineChanged?: boolean; label?: string }) => void;
  /** Register a card's resolver. Returns the unregister fn; unregistering
   *  clears the focus if this card held it. */
  register: (id: string, resolver: Resolver) => () => void;
  /** Resolve the focused card's CURRENT targets; null when nothing is
   *  focused or the focused card's resolver went away. */
  resolveFocused: () => Lt2CardTargets | null;

  // ── Level cursor (Sniper v2 Phase 2) ─────────────────────────────────────
  /**
   * WHERE on the focused card the LT2 keys act. Reset to `LT2_CURSOR_DEFAULT`
   * by every focus CHANGE (including the clear), so a cursor can never survive
   * onto a card the trader did not aim it at. Re-aiming the same card leaves it
   * alone — a card that re-renders has not moved.
   */
  cursor: Lt2Cursor;
  /** J / K — relative move, clamped to the addressable range. */
  moveCursor: (delta: number) => void;
  /** 1–9 / 0 — absolute level, clamped the same way. */
  setCursorLevel: (level: number) => void;
  /** H / L — move the cursor to the other book, keeping the level (re-clamped
   *  against the new slot, which may publish fewer rungs). */
  moveCursorSlot: (delta: number) => void;
  setCursorSlot: (slot: Lt2CursorSlot) => void;
  resetCursor: () => void;

  // ── Ghost legs ───────────────────────────────────────────────────────────
  /** What the ARMED op would target, or null when nothing is armed. */
  ghostLegs: Lt2GhostLeg[] | null;
  setGhostLegs: (legs: readonly Lt2GhostLeg[] | null) => void;
  clearGhostLegs: () => void;

  // ── Execution-band view state the nav keys drive ──────────────────────────
  marketTab: Lt2MarketTab;
  cycleMarketTab: () => void;
  setMarketTab: (tab: Lt2MarketTab) => void;
  dockPage: Lt2DockPage;
  toggleDockPage: () => void;

  // ── Modal fence ──────────────────────────────────────────────────────────
  /** Number of mounted, open atoms/Modal instances. The hotkey listener
   *  suppresses page-wide while this is > 0 — the one page-level fact it
   *  used to learn by a full-document query on every keydown. A surface
   *  that grows its own modal (no current one does) registers here too. */
  modalOpenCount: number;
  noteModalOpened: () => void;
  noteModalClosed: () => void;

  // ── Painted-focus claims ─────────────────────────────────────────────────
  /** True while some card OTHER than `focusedCardId` last rendered itself
   *  focused — the DOM carries a foreign `data-lt2-focused` mark (stale
   *  paint between a focus change and its commit, or two cards claiming
   *  focus, the documented pilot stop condition). The listener refuses on
   *  this flag exactly as it refused on finding that row by a document
   *  query. */
  focusPaintAmbiguous: boolean;
  /** From useLt2Card's post-commit effect, with the boolean the card just
   *  RENDERED — which is the value every mark painter derives from. */
  notePainted: (id: string, painted: boolean) => void;

  // ── Card root (scoped DOM lookup) ────────────────────────────────────────
  /** The lowest common ancestor of the rows a card paints, discovered by
   *  useLt2Card when the card takes focus. Null = unknown → the listener
   *  falls back to the document-wide query. */
  setCardRoot: (id: string, root: Element | null) => void;
  /** The focused card's root, or null when none is registered / it has
   *  left the document. */
  focusedCardRoot: () => Element | null;

  // ── Row visibility flags ─────────────────────────────────────────────────
  noteRowVisibility: (row: Element, visible: boolean, atMs: number) => void;
  readRowVisibility: (row: Element) => Lt2RowVisibility | undefined;
  /** Any scroll (capture, so inner containers count) or resize since a
   *  flag was stamped makes that flag stale — the geometry it measured may
   *  no longer hold, and the observer's fresh measurement has not arrived. */
  noteViewportChange: (atMs: number) => void;
  lastViewportChangeAtMs: () => number;

  // ── Lost aim (D19) ───────────────────────────────────────────────────────
  /** D19 — the label of an aimed card that left the board (its card
   *  unregistered, or `clearFocusIf` dropped it), so the next bound press can
   *  say so instead of being silent. Any deliberate aim change (a new aim,
   *  `clearFocus`) forgets it. */
  lostAim: string | null;
  lostAimReason: 'market-gone' | 'line-changed' | null;
  /** D19 — read and forget `lostAim`: one refusal per lost aim. */
  takeLostAim: () => string | null;
}

const _resolvers = new Map<string, Resolver>();
const _painted = new Set<string>();
const _roots = new Map<string, Element>();
const _rowVisibility = new WeakMap<Element, Lt2RowVisibility>();
let _lastViewportChangeAtMs = 0;

function ambiguousFor(focusedId: string | null): boolean {
  for (const id of _painted) if (id !== focusedId) return true;
  return false;
}

/** D19 — the label a card would show for itself, read from its own resolver
 *  as it leaves. A card that cannot answer gets a generic name. */
function labelOf(resolver: Resolver | undefined): string {
  try {
    const label = resolver?.()?.cardLabel;
    if (typeof label === 'string' && label !== '') return label;
  } catch {
    /* a card mid-unmount may not answer */
  }
  return 'The aimed card';
}

export const useLt2FocusStore = create<Lt2FocusStore>((set, get) => {
  /** Every focus transition re-derives the ambiguity flag against the NEW
   *  focus, so the flag and the id can never be read out of step. A focus
   *  CHANGE additionally drops the level cursor back to top of book and clears
   *  any armed op's ghost legs: both describe a card, and carrying either onto
   *  a different card would aim a key (or paint a plan) at a rung nobody
   *  chose. Re-aiming the same card is not a change and leaves them. */
  const setFocused = (
    id: string | null,
    lostAim: string | null = null,
    lostAimReason: Lt2FocusStore['lostAimReason'] = null,
  ) => {
    const changed = get().focusedCardId !== id;
    set({
      focusedCardId: id,
      focusPaintAmbiguous: ambiguousFor(id),
      // D19 — a deliberate aim change forgets a lost aim; a loss records it,
      // with why (#1143: the market left, or its displayed line moved).
      lostAim,
      lostAimReason: lostAim != null ? lostAimReason : null,
      ...(changed ? { cursor: LT2_CURSOR_DEFAULT, ghostLegs: null } : {}),
    });
  };

  /**
   * The addressable ceiling for a slot: the published rung count when the
   * focused card publishes `levels`, else the ultrawide 12-per-side design
   * bound. Clamping — rather than refusing — is deliberate: a cursor key is
   * navigation, and a cursor that visibly stops at the end of the ladder is
   * self-explaining. Only FIRING needs a proof, and it has its own.
   */
  const clampLevel = (level: number, slot: Lt2CursorSlot): number => {
    if (!Number.isFinite(level)) return 0;
    const wanted = Math.trunc(level);
    if (wanted <= 0) return 0;
    const targets = get().resolveFocused();
    const published = (slot === 'a' ? targets?.a : targets?.d)?.levels;
    const ceiling = published != null ? published.length - 1 : LT2_CURSOR_MAX_LEVEL;
    return Math.min(wanted, Math.max(0, ceiling));
  };

  return {
    focusedCardId: null,
    presentationSeq: 0,
    modalOpenCount: 0,
    focusPaintAmbiguous: false,
    lostAim: null,
    lostAimReason: null,
    cursor: LT2_CURSOR_DEFAULT,
    ghostLegs: null,
    marketTab: 'ml',
    dockPage: 'context',

    notePresentation: () => set((s) => ({ presentationSeq: s.presentationSeq + 1 })),

    takeLostAim: () => {
      const label = get().lostAim;
      if (label != null) set({ lostAim: null, lostAimReason: null });
      return label;
    },

    setFocus: (id) => {
      if (!_resolvers.has(id)) return; // only registered cards are focusable
      setFocused(id);
    },

    toggleFocus: (id) => {
      if (!_resolvers.has(id)) return; // only registered cards are focusable
      setFocused(get().focusedCardId === id ? null : id);
    },

    focusIfFree: (id) => {
      if (!_resolvers.has(id)) return;
      const current = get().focusedCardId;
      if (current === null || current === id) setFocused(id);
    },

    clearFocus: () => setFocused(null),

    clearFocusIf: (id, opts) => {
      if (get().focusedCardId !== id) return;
      // D19 — `marketGone`: the market under the aim disappeared, which is not
      // the trader's choice, so the next press says so. Without it this is a
      // deliberate clear (the aim chip's ✕) and nothing is remembered.
      // #1143 — `lineChanged`: the card's displayed line moved under an
      // unpriced aim; the caller names the line it was aimed at (`label`).
      const lost = opts?.marketGone || opts?.lineChanged;
      setFocused(
        null,
        lost ? (opts.label ?? labelOf(_resolvers.get(id))) : null,
        opts?.marketGone ? 'market-gone' : opts?.lineChanged ? 'line-changed' : null,
      );
    },

    register: (id, resolver) => {
      _resolvers.set(id, resolver);
      return () => {
        // Another register may have replaced this id (StrictMode double
        // mount); only delete if we still own it.
        if (_resolvers.get(id) === resolver) {
          // D19 — the aimed card left the board; remember what it was.
          const lostAim = get().focusedCardId === id ? labelOf(resolver) : null;
          _resolvers.delete(id);
          _roots.delete(id);
          _painted.delete(id);
          if (lostAim != null) setFocused(null, lostAim, 'market-gone');
          else set({ focusPaintAmbiguous: ambiguousFor(get().focusedCardId) });
        }
      };
    },

    resolveFocused: () => {
      const id = get().focusedCardId;
      if (id == null) return null;
      const resolver = _resolvers.get(id);
      return resolver ? resolver() : null;
    },

    moveCursor: (delta) =>
      set((s) => ({
        cursor: { ...s.cursor, level: clampLevel(s.cursor.level + delta, s.cursor.slot) },
      })),

    setCursorLevel: (level) =>
      set((s) => ({ cursor: { ...s.cursor, level: clampLevel(level, s.cursor.slot) } })),

    moveCursorSlot: (delta) =>
      set((s) => {
        // Two books per card today (the soccer draw column is design §4.1
        // future work), so any non-zero delta is "the other one".
        if (delta === 0) return { cursor: s.cursor };
        const slot: Lt2CursorSlot = s.cursor.slot === 'a' ? 'd' : 'a';
        return { cursor: { slot, level: clampLevel(s.cursor.level, slot) } };
      }),

    setCursorSlot: (slot) =>
      set((s) => ({ cursor: { slot, level: clampLevel(s.cursor.level, slot) } })),

    resetCursor: () => set({ cursor: LT2_CURSOR_DEFAULT }),

    setGhostLegs: (legs) => set({ ghostLegs: legs == null ? null : [...legs] }),
    clearGhostLegs: () => set({ ghostLegs: null }),

    cycleMarketTab: () =>
      set((s) => {
        const at = LT2_MARKET_TAB_ORDER.indexOf(s.marketTab);
        return { marketTab: LT2_MARKET_TAB_ORDER[(at + 1) % LT2_MARKET_TAB_ORDER.length] };
      }),
    setMarketTab: (tab) => set({ marketTab: tab }),
    toggleDockPage: () =>
      set((s) => ({ dockPage: s.dockPage === 'context' ? 'control' : 'context' })),

    noteModalOpened: () => set((s) => ({ modalOpenCount: s.modalOpenCount + 1 })),
    noteModalClosed: () => set((s) => ({ modalOpenCount: Math.max(0, s.modalOpenCount - 1) })),

    notePainted: (id, painted) => {
      if (painted) _painted.add(id);
      else _painted.delete(id);
      const next = ambiguousFor(get().focusedCardId);
      if (next !== get().focusPaintAmbiguous) set({ focusPaintAmbiguous: next });
    },

    setCardRoot: (id, root) => {
      if (root == null) _roots.delete(id);
      else _roots.set(id, root);
    },

    focusedCardRoot: () => {
      const id = get().focusedCardId;
      if (id == null) return null;
      const root = _roots.get(id);
      return root != null && root.isConnected ? root : null;
    },

    noteRowVisibility: (row, visible, atMs) => {
      _rowVisibility.set(row, { visible, atMs });
    },
    readRowVisibility: (row) => _rowVisibility.get(row),
    noteViewportChange: (atMs) => {
      if (atMs > _lastViewportChangeAtMs) _lastViewportChangeAtMs = atMs;
    },
    lastViewportChangeAtMs: () => _lastViewportChangeAtMs,
  };
});
